/** Data hooks for Phase 6: workflow fields, approvals, calls, samples, trade shows and the sales dashboard. */
import { useQuery } from "@tanstack/react-query";

import { api, qs } from "./client";
import type { OrderSummary, Ref, UserRef } from "./types";

export interface FieldDef { key: string; label: string; type: "text" | "number" | "bool" | string; group: string }

export interface PendingQuote {
  id: string; number: string; version: number; title: string; currency: string; grand_total: number;
  account: Ref | null; deal: Ref | null; owner: UserRef | null; reasons: string[]; note: string | null; requested_at: string | null;
}
export interface HeldOrder extends OrderSummary { reason: string | null }
export interface Approvals { quotes: PendingQuote[]; orders: HeldOrder[] }

export interface TelephonyStatus { click_to_call: boolean; your_phone: string | null; sms: boolean }
export interface CallRecord {
  id: string; direction: "in" | "out"; status: string; source: "provider" | "manual"; contact: { id: string; full_name: string } | null;
  deal: Ref | null; user: UserRef | null; from_number: string | null; to_number: string | null; started_at: string | null;
  duration_seconds: number | null; outcome: string | null; notes: string | null; recording_url: string | null; error: string | null; created_at: string;
}

export type SampleStatus = "requested" | "dispatched" | "delivered" | "on_trial" | "approved" | "rejected";
export interface Sample {
  id: string; kind: "sample" | "trial"; status: SampleStatus; deal: Ref; account: Ref | null; product: Ref | null; owner: UserRef | null;
  description: string; quantity: number; uom: string; chargeable: boolean; dispatched_on: string | null; courier: string | null;
  tracking_number: string | null; feedback_due: string | null; feedback: string | null; decided_at: string | null; overdue: boolean;
  created_at: string; updated_at: string;
}

export interface TradeShow {
  id: string; name: string; location: string | null; starts_on: string | null; ends_on: string | null; owner: UserRef | null;
  leads: number; hot_leads: number; pipeline: number; won: number; created_at: string;
}
export interface CapturedLead {
  id: string; status: "created" | "updated" | "duplicate" | string; name: string; company: string | null; temperature: "hot" | "warm" | "cold";
  interest: string | null; contact: Ref | null; deal: Ref | null; captured_by: string | null; created_at: string;
}
export interface CardScan { full_name: string | null; job_title: string | null; company: string | null; phone: string | null; email: string | null; website: string | null; city: string | null }

export interface SalesReport {
  currency: string; date_from: string; date_to: string;
  kpis: {
    new_deals: number; new_deal_value: number; won_count: number; won_value: number; lost_count: number; win_rate: number | null;
    avg_cycle_days: number | null; quotes_sent: number; quotes_accepted: number; orders_count: number; orders_value: number;
    calls: number; visits: number; sla_met_pct: number | null; on_time_delivery_pct: number | null;
  };
  funnel: { stage: string; reached: number; value: number }[];
  sources: { source: string; deals: number; value: number; won_value: number }[];
  reps: { user_id: string; name: string; won_value: number; won_count: number; open_pipeline: number; quotes_sent: number; calls: number; visits: number }[];
  months: { month: string; won_value: number; orders_value: number }[];
  sla: { channel: string; replies: number; met: number; pct: number | null; avg_minutes: number | null }[];
}
export interface ReportFilters { period: string; date_from?: string; date_to?: string; owner_id?: string; territory_id?: string }

export const useAutomationFields = () =>
  useQuery({ queryKey: ["automation-fields"], queryFn: () => api<FieldDef[]>("GET", "/automations/fields"), staleTime: 300_000 });

export const useApprovals = (enabled = true) =>
  useQuery({ queryKey: ["approvals"], queryFn: () => api<Approvals>("GET", "/approvals"), enabled, refetchInterval: 60_000 });

export const useTelephony = () => useQuery({ queryKey: ["telephony"], queryFn: () => api<TelephonyStatus>("GET", "/telephony") });
export const useCalls = (filters: { contact_id?: string; deal_id?: string }) =>
  useQuery({ queryKey: ["calls", filters], queryFn: () => api<CallRecord[]>("GET", `/calls${qs(filters)}`) });

export const useSamples = (filters: { deal_id?: string; status?: string }) =>
  useQuery({ queryKey: ["samples", filters], queryFn: () => api<Sample[]>("GET", `/samples${qs(filters)}`) });

export const useShows = () => useQuery({ queryKey: ["trade-shows"], queryFn: () => api<TradeShow[]>("GET", "/trade-shows") });
export const useShowLeads = (id: string | null) =>
  useQuery({ queryKey: ["show-leads", id], queryFn: () => api<CapturedLead[]>("GET", `/trade-shows/${id}/leads`), enabled: !!id });

export const useSalesReport = (filters: ReportFilters) =>
  useQuery({ queryKey: ["sales-report", filters], queryFn: () => api<SalesReport>("GET", `/reports/sales${qs({ ...filters })}`) });
